/* eslint-disable @typescript-eslint/member-ordering */
import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, filter, firstValueFrom, map, switchMap, take, Subject } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';
import { currentUserData } from 'src/app/authentication/store/authentication.reducer';
import { MatomoService } from 'src/app/core/services/matomo.service';
import { GetChecks } from 'src/app/dashboard/store/dashboard.actions';
import { IProject } from 'src/app/discrepancies/models/discrepancies.model';
import {
  ClearState,
  GetBookmarks,
  GetComments,
  GetDiscrepancies,
  GetFiles,
  GetProject
} from 'src/app/discrepancies/store/discrepancies.actions';
import { getProject } from 'src/app/discrepancies/store/discrepancies.reducer';
import { ClearTabs } from 'src/app/discrepancies/store/tabs/tabs.actions';
import { ClearTasksFilters, SetTasksOrder } from 'src/app/discrepancies/store/tasks/tasks.actions';
import { GetParameters } from 'src/app/parameters/store/parameter.actions';
import { getBooleanParameter, getStringParameter } from 'src/app/parameters/store/parameter.reducer';
import { IRole } from 'src/app/permissions/models/role.model';
import { GeneralStatus } from 'src/app/spa/models/spa-project.model';
import { ClearFilterState } from 'src/app/spa/store/project-filters/project-filters.actions';
import { GetCheckList } from 'src/app/spa/store/spa-project.actions';
import { selectRouteParam } from 'src/app/store/app.actions';
import { IUserModel, UserTypes } from 'src/app/users/models/user.model';
import { GetLocalPreferences, GetProjectUsersList, UpdateRecentlyViewedProjects } from 'src/app/users/store/user.actions';
import { ClearLabelingData } from 'src/app/validation/store/project-labels/project-labels.actions';
import { GetOutputChecks } from 'src/app/validation/store/validation/validation.actions';
import { environment } from 'src/environments/environment';
import { getInProgress } from 'src/app/algo-settings/store/algo-settings.selectors';
import { GetHasMetadataData } from 'src/app/algo-settings/store/algo-settings.actions';
import { IMenuPosition } from '../../models/menu.model';

@Component({
  selector: 'app-project',
  templateUrl: './project.component.html',
  styleUrls: ['./project.component.scss'],
  standalone: false
})
export class ProjectComponent implements OnInit, OnDestroy {
  private store = inject(Store);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private matomoService = inject(MatomoService);

  @Input() id: string;
  projectIdSubject = new BehaviorSubject<string | null>(null);
  projectId$ = this.projectIdSubject.asObservable();

  project$ = this.projectId$.pipe(
    filter((id) => !!id),
    switchMap((id) => this.store.select(getProject).pipe(filter((project) => project?.id === id)))
  );

  readonly currentUser$ = this.store.select(currentUserData);
  readonly isReview$ = this.store.select(getStringParameter('ENTITLMODE')).pipe(map((mode) => mode === 'review'));
  readonly compareEnabled$ = this.store.select(getBooleanParameter('ENTCOMPARE'));
  readonly allowTracker$ = this.store.select(getBooleanParameter('QCTRACKER1'));
  readonly allowMetaCuratorType$ = this.store.select(getStringParameter('RECALCTYPE'));
  readonly inAlgoProcess$ = this.store.select(getInProgress);

  menuGroups$ = combineLatest([
    this.project$,
    this.currentUser$,
    this.store.select(selectRouteParam('taskId')),
    this.compareEnabled$,
    this.allowTracker$,
    this.allowMetaCuratorType$,
    this.inAlgoProcess$
  ]).pipe(
    map(([project, user, taskId, allowCompare, allowTracker, allowMetaCuratorType, inAlgoProcess]) =>
      this.createMenu(project, user, taskId, allowCompare, allowTracker, allowMetaCuratorType, inAlgoProcess)
    )
  );
  private unsubscribe$: Subject<void> = new Subject<void>();

  secondaryMenu$ = combineLatest([this.menuGroups$, this.router.events.pipe(startWith(this.router.url))]).pipe(
    map(([menuGroups]) => {
      const url = this.router.url;
      const menus = menuGroups.flat();
      const activeMenu = menus.find((menu) => url.includes(menu.path));
      return activeMenu?.submenu;
    })
  );

  ngOnInit() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        switchMap(() => this.route.firstChild.paramMap),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((params) => {
        const id = params.get('id');
        if (id && id !== this.projectIdSubject.value) {
          this.projectIdSubject.next(id);
          this.loadProjectData(id);
        }
      });

    if (this.id) {
      this.projectIdSubject.next(this.id);
      this.loadProjectData(this.id);
    }

    this.project$.pipe(takeUntil(this.unsubscribe$)).subscribe((project) => {
      if (project) {
        this.store.dispatch(
          UpdateRecentlyViewedProjects({
            projectId: project.id,
            projectName: project.name
          })
        );
      }
    });
  }

  userCanViewMetaCurator(user, recalcType): boolean {
    const isValidRecalcType = (recalcType === 'VV' && user.me?.type !== UserTypes.USER) || recalcType === 'BOTH';
    return isValidRecalcType && user.effectiveRole.permissions.interactiveAIPageView;
  }

  createMenu(
    project?: IProject,
    user?: { me: IUserModel; effectiveRole: IRole },
    taskId?: string,
    allowCompare = false,
    allowTracker = false,
    allowMetaCuratorType = 'NA',
    inAlgoProcess = false
  ): IMenuPosition[][] {
    const canAccessOverview = user && user.effectiveRole?.permissions['overviewView'];
    const canAccessReview = user && project && this.canAccessReview(project, user.me);
    const canAccessMetadataCurator =
      user &&
      user.effectiveRole?.permissions.interactiveAIPageView &&
      ((allowMetaCuratorType === 'VV' && user.me?.type !== UserTypes.USER) || allowMetaCuratorType === 'BOTH');
    const hasMetadataData = project.analysisType === 'gen ai track';
    const canAccessTaskPage = user?.effectiveRole?.permissions.ddrAccessTasksPage;
    const canSeeValidation = user?.effectiveRole?.permissions.oldDiscrepancyPageAccess;
    const id = taskId ?? '';
    const previousAnalysis = project?.analyzes?.length > 1 ? this.getPreviousAnalysis(project) : null;
    const analyzesSubMenu = this.createAnalyzesSubMenu(project);
    return [
      [
        {
          name: 'Project Overview',
          path: 'overview',
          icon: 'monitoring',
          submenu: {
            name: 'Overview',
            items: [
              {
                name: 'Task Overview',
                path: `overview/tasks`
              },
              {
                name: 'Comparison Overview',
                path: `overview/comparison`,
                hidden: !allowCompare
              },
              // {
              //   name: 'Tracker Overview',
              //   path: `overview/tracker`,
              //   hidden: !allowTracker
              // },
              // {
              //   name: 'Project Snapshot',
              //   path: `overview/snapshot`
              // },
              {
                name: 'Project Info',
                path: `overview/info`
              },
              {
                name: 'Visual Validation',
                path: `overview/validation`,
                hidden: !canSeeValidation
              }
            ]
          },
          disabled: !canAccessOverview,
          hidden: !canAccessOverview
        }
      ],
      [
        {
          name: 'GenAI Validation',
          path: 'metadata-curator',
          icon: 'metadata-curator',
          svgIcon: true,
          tooltip: !hasMetadataData ? 'Available only with Gen AI Analysis' : undefined,
          disabled: !canAccessMetadataCurator || !hasMetadataData,
          hidden: !canAccessMetadataCurator
        },
        {
          name: 'File Review',
          path: `review/files/${id}`,
          icon: 'inventory',
          disabled: !canAccessReview || inAlgoProcess
        },
        {
          name: 'Task Manager',
          path: `review/tasks/${id}`,
          icon: 'task_alt',
          disabled: !canAccessReview || inAlgoProcess,
          hidden: !canAccessTaskPage
        },
        {
          name: 'Analysis Comparison',
          path: `review/compare`,
          icon: 'balance',
          badge: '\u200B',
          disabled: !canAccessReview || inAlgoProcess,
          hidden: !allowCompare || !previousAnalysis,
          submenu: analyzesSubMenu
        },
        {
          name: 'Visual Validation',
          path: 'validation',
          icon: 'rule',
          disabled: !canAccessReview || inAlgoProcess,
          hidden: !canSeeValidation
        }
      ],
      [
        {
          name: 'Progress Tracker',
          path: `tracker`,
          icon: 'account_tree',
          disabled: !canAccessReview,
          hidden: !allowTracker
        },
        {
          name: 'Activity Log',
          path: 'activity-log',
          icon: 'history'
        }
      ]
    ];
  }

  createAnalyzesSubMenu(project: IProject) {
    if (!project.analyzes || project.analyzes?.length <= 2) return null;
    const currentAnalysis = project.analyzes[project.analyzes.length - 1];
    const items = project.analyzes
      .filter((analysis) => analysis.index !== currentAnalysis.index)
      .sort(({ index: a }, { index: b }) => b - a)
      .map((analysis) => ({
        name: `${currentAnalysis.label} vs ${analysis.label}`,
        path: `review/compare/${analysis?.id}`
      }));
    return { name: 'Comparison', items };
  }

  getPreviousAnalysis(project: IProject) {
    return project.analyzes?.find(({ index }) => index === project.analyzes.length - 1);
  }

  canAccessReview(project: IProject, user: IUserModel) {
    if (!user) return false;
    const status = project.status as GeneralStatus;
    const isReviewer = user.type === UserTypes.ADMIN || user.type === UserTypes.VISUAL_VALIDATION;
    const afterMinimumCreateDate = new Date(environment.spaMinimumCreatedDate) <= new Date(project.lastAnalysisDate);
    const readyForReviewer = [GeneralStatus.IN_REVIEW, GeneralStatus.DONE].includes(status);
    const readyForUser = status === GeneralStatus.DONE;
    return (isReviewer && readyForReviewer) || (afterMinimumCreateDate && readyForUser);
  }

  hasVisibleMenu(menuPositions: IMenuPosition[]) {
    return !menuPositions.every((menu) => menu.hidden);
  }

  async registerMatomoSelectMenu(name: string) {
    this.matomoService.trackEvent('Navigation bar', 'Quick navigation', `Navigation bar - ${name}`);
    if (name !== 'Analysis Comparison') return;
    const project = await firstValueFrom(this.project$);
    if (project.analyzes.length > 2) return;
    this.matomoService.trackEvent('New discrepancies page', 'Quick navigation', 'Comparison feature choose analysis');
  }

  registerMatomoCompareMode() {
    this.matomoService.trackEvent('New discrepancies page', 'Quick navigation', 'Comparison feature choose analysis');
  }

  async refreshDiscrepancies() {
    if (this.router.url.endsWith('/validation')) {
      const project = await firstValueFrom(this.project$);
      this.store.dispatch(GetDiscrepancies({ projectID: project.id }));
    }
  }

  ngOnDestroy() {
    this.store.dispatch(ClearState());
    this.store.dispatch(ClearFilterState());
    this.store.dispatch(ClearTabs());
    this.store.dispatch(ClearTasksFilters());
    this.store.dispatch(ClearLabelingData());
    this.store.dispatch(SetTasksOrder({ ids: null }));

    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.projectIdSubject.complete();
  }

  private loadProjectData(projectID: string): void {
    this.store.dispatch(ClearState());
    this.store.dispatch(ClearLabelingData());
    this.store.dispatch(GetProject({ projectID }));
    this.store.dispatch(GetCheckList({ payload: projectID }));
    this.store.dispatch(GetProjectUsersList({ payload: projectID }));
    this.store.dispatch(GetParameters());
    this.store.dispatch(GetBookmarks({ projectID }));
    this.store.dispatch(GetChecks());
    this.store.dispatch(GetOutputChecks({ projectID }));

    combineLatest([
      this.currentUser$.pipe(filter(({ me }) => !!me)),
      this.project$.pipe(filter((project) => !!project.status)),
      this.allowMetaCuratorType$
    ])
      .pipe(take(1), takeUntil(this.unsubscribe$))
      .subscribe(([user, project, recalcType]) => {
        if (this.userCanViewMetaCurator(user, recalcType)) {
          this.store.dispatch(GetHasMetadataData({ projectID }));
        }
        if (!this.canAccessReview(project, user.me)) return;
        this.store.dispatch(GetFiles({ projectID }));
        this.store.dispatch(GetDiscrepancies({ projectID }));
        this.store.dispatch(GetComments({ projectID }));
        if (user?.me && project?.id) {
          this.store.dispatch(GetLocalPreferences());
        }
      });
  }
}
